import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch,
} from '@mui/material';
import api from '../../utils/api';
import { useBusiness } from '../../context/BusinessContext';
import { formatGBPFromPounds } from '../../utils/currency';
import { getPriceSuffix } from '../../utils/priceDisplay';

function BusinessSubscriptions({ onSubscriptionUpdate }) {
  const { selectedBusiness, hasPermission } = useBusiness();
  const businessId = selectedBusiness?.business?._id;

  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [autoRenew, setAutoRenew] = useState(true);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  const [contractInfo, setContractInfo] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchAllData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      // Fetch subscription details, contract status, and plans
      const [subscriptionResponse, contractStatusResponse] = await Promise.all([
        api.get(`/api/business/${businessId}/subscriptions`).catch(() => ({ data: { members: [] } })),
        api.get(`/api/business/${businessId}/subscriptions/contract-status`).catch(() => ({ data: { exists: false } })),
        fetchPlans() // Fetch plans when component loads
      ]);
      
      setSubscription(subscriptionResponse.data);
      
      // Set auto-renewal status
      if (subscriptionResponse.data?.businessSubscription) {
        setAutoRenew(subscriptionResponse.data.businessSubscription.autoRenew !== false); // Default to true
      }
      
      // Set contract info - prioritize contract-status endpoint, fallback to subscription response
      if (contractStatusResponse.data?.exists) {
        setContractInfo(contractStatusResponse.data);
      } else if (subscriptionResponse.data?.contractInfo) {
        // Fallback: use contract info from subscription response if contract-status endpoint didn't return it
        setContractInfo(subscriptionResponse.data.contractInfo);
      } else {
        setContractInfo(null);
      }
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/api/billing/plans');
      // Filter for supplier plans (per-user billing)
      const supplierPlans = response.data.filter(plan => {
        const isSupplierPlan = plan.userType === 'supplier' || plan.userType === 'both';
        const isPerUserBilling = plan.billingModel === 'per_user';
        return isSupplierPlan || isPerUserBilling;
      });
      setPlans(supplierPlans);
      return supplierPlans; // Return for Promise.all
    } catch (error) {
      return []; // Return empty array for Promise.all
    }
  };

  const handleToggleAutoRenew = async () => {
    try {
      setTogglingAutoRenew(true);
      const newAutoRenew = !autoRenew;
      const _response = await api.put(`/api/business/${businessId}/subscriptions/auto-renew`, {
        autoRenew: newAutoRenew
      });
      
      setAutoRenew(newAutoRenew);
      // Refresh data and notify parent
      fetchAllData();
      if (onSubscriptionUpdate) {
        onSubscriptionUpdate();
      }
    } catch (error) {} finally {
      setTogglingAutoRenew(false);
    }
  };

  const getPlanDisplayName = (planKey) => {
    const plan = plans.find(p => p.key === planKey);
    return plan ? plan.displayName || plan.name : planKey;
  };

  if (!hasPermission('view_financials')) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
        <Typography variant="body1">
          You don't have permission to manage subscriptions.
        </Typography>
      </Box>
    );
  }

  if (loading && !subscription) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Note: Success/Error alerts removed - parent BusinessBilling component handles all toasts to avoid duplicates */}
      {/* Renewal Information - Like ClickUp */}
      {subscription && subscription.exists ? (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Renewal information
            </Typography>
            
            {/* Summary Row */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Your plan</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {subscription.businessSubscription?.currentPlanMembershipLevelKey 
                    ? getPlanDisplayName(subscription.businessSubscription.currentPlanMembershipLevelKey)
                    : subscription.businessSubscription?.currentPlanMembershipLevelKey || 'No plan'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Billing Cycle</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {subscription.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Next billing date</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {subscription.nextBillingDate 
                    ? new Date(subscription.nextBillingDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric'
                      })
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Contract End</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {(() => {
                    // Show contract end date when we have it (fixed-term plans)
                    const contractEndDate = contractInfo?.contractEndDate || subscription.contractInfo?.contractEndDate;
                    const isFixedTerm = contractInfo?.isFixedTerm ?? subscription.contractInfo?.isFixedTerm;
                    const dateToShow = (isFixedTerm && contractEndDate) ? contractEndDate : null;
                    
                    return dateToShow 
                      ? new Date(dateToShow).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        })
                      : '-';
                  })()}
                </Typography>
                {/* Auto Renew Toggle */}
                {subscription.activeMembers > 0 && hasPermission('manage_financials') && (
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Switch
                      checked={autoRenew}
                      onChange={handleToggleAutoRenew}
                      disabled={togglingAutoRenew}
                      size="small"
                      color="primary"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Auto Renew {autoRenew ? 'On' : 'Off'}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatGBPFromPounds(subscription.totalWithAddons !== undefined ? subscription.totalWithAddons : subscription.totalMonthlyAmount || 0)}
                  {getPriceSuffix(subscription.totalMonthlyAmount || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
                </Typography>
              </Box>
            </Box>

            {/* Product Breakdown Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Unit price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Members Product */}
                  {subscription.businessSubscription?.contractMinSeats !== undefined ? (
                    // Seat-based billing
                    (<TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {(() => {
                            const planKey = subscription.businessSubscription?.currentPlanMembershipLevelKey;
                            if (!planKey) {
                              return 'Members';
                            }
                            const plan = plans.find(p => p.key === planKey);
                            if (!plan) {
                              return planKey; // Fallback to key if plan not found
                            }
                            return plan.displayName || plan.name || planKey;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // For seat-based billing, show active seats (not effective quantity which includes contract min)
                            // The backend should return activeMembers as the count of active seats
                            const activeSeats = subscription.activeMembers ?? subscription.subscriptionQuantity ?? 0;
                            return activeSeats;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            // Use unit price from Stripe if available
                            if (subscription.unitPrice != null && subscription.unitPrice > 0) {
                              return `${formatGBPFromPounds(subscription.unitPrice)} per member ${getPriceSuffix(subscription.unitPrice, subscription.billingCycle === 'yearly' ? 'year' : 'month')}`;
                            }
                            // Fallback: calculate from plan
                            const planKey = subscription.businessSubscription?.currentPlanMembershipLevelKey;
                            const plan = plans.find(p => p.key === planKey);
                            if (!plan) return '-';
                            const unitPrice = subscription.billingCycle === 'yearly' 
                              ? plan.price * 12 * (1 - (plan.annualDiscountPercent || 10) / 100)
                              : plan.price;
                            return `${formatGBPFromPounds(unitPrice)} per member ${getPriceSuffix(unitPrice, subscription.billingCycle === 'yearly' ? 'year' : 'month')}`;
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {formatGBPFromPounds(subscription.totalMonthlyAmount || 0)}
                          {getPriceSuffix(subscription.totalMonthlyAmount || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
                        </Typography>
                      </TableCell>
                    </TableRow>)
                  ) : (
                    // Legacy per-member billing - show each member as a row
                    (subscription.members?.filter(m => m.isActive && !m.removedAt).map((member, index) => (
                      <TableRow key={member.businessMemberId || index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {member.businessMember?.user?.firstName 
                              ? `${member.businessMember.user.firstName} ${member.businessMember.user.lastName || ''}`.trim()
                              : member.businessMember?.invitedEmail || 'Member'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">1</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatGBPFromPounds(member.price || 0)} per member {getPriceSuffix(member.price || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {formatGBPFromPounds(member.price || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )))
                  )}
                  
                  {/* Addons */}
                  {subscription.addons && subscription.addons.length > 0 && subscription.addons.map((addon, index) => (
                    <TableRow key={`addon-${addon.addonKey}-${index}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {addon.name || addon.addonKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {addon.quantity || 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatGBPFromPounds(addon.price || 0)} {getPriceSuffix(addon.price || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                            {formatGBPFromPounds(addon.totalPrice || (addon.price || 0) * (addon.quantity || 1))}
                          {getPriceSuffix(addon.price || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total Row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={2} borderTop="1px solid" borderColor="divider">
              <Typography variant="body1" fontWeight={600}>Total</Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatGBPFromPounds(subscription.totalWithAddons !== undefined ? subscription.totalWithAddons : subscription.totalMonthlyAmount || 0)}
                {getPriceSuffix(subscription.totalMonthlyAmount || 0, subscription.billingCycle === 'yearly' ? 'year' : 'month')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
              No active subscription. Select a plan from the Plans & Pricing section above to get started.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default BusinessSubscriptions;
